import dayjs from 'dayjs';
import i18n from 'i18next';
import {SelectOptionType} from '@guestapp/sdk';
import {getCurrentLocale} from 'utils/common';
import {LocationType} from 'utils/types';
import {CustomField} from './types';
import {COUNTRY_CODES} from 'utils/constants';
import {PATTERNS} from '@guestapp/core';
import {dniValidation, nieValidation} from 'utils/docTypes';

function goToExternalLink(url: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.click();
}

function getDocNumberPattern(country?: string, isPoliceActivated?: boolean) {
  if (country === COUNTRY_CODES.italy && !isPoliceActivated) {
    return PATTERNS.docNumberWithDots;
  }

  return PATTERNS.docNumber;
}

function validateDNI(number = '', type: string | number = '') {
  const DNI_STRING = 'TRWAGMYFPDXBNJZSQVHLCKE';
  const DNI_DIVISION_NUMBER = 23;

  if (!PATTERNS.dniAndDl.test(number)) {
    return i18n.t('doc_number_dni_format_error');
  }

  const extractedNumbers = Number(number.slice(0, -1));
  const extractedChar = number.substr(number.length - 1).toUpperCase();
  const expectedChar = DNI_STRING[extractedNumbers % DNI_DIVISION_NUMBER];

  if (expectedChar !== extractedChar) {
    return i18n.t('doc_number_dni_letter_error');
  }

  return true;
}

function validateNIE(number = '') {
  const NIE_STRING = 'TRWAGMYFPDXBNJZSQVHLCKE';
  const NIE_DIVISION_NUMBER = 23;
  const firstLetterValue: {[key: string]: string} = {
    X: '0',
    Y: '1',
    Z: '2',
  };

  if (!PATTERNS.nie.test(number)) {
    return i18n.t('doc_number_nie_format_error');
  }

  const withoutLetters = number.slice(1, -1);
  const lastSymbol = number[number.length - 1].toUpperCase();

  const firstLetter: string = number[0].toUpperCase();
  const replacedFirstLetter = firstLetterValue[firstLetter];

  if (replacedFirstLetter === undefined) {
    return i18n.t('doc_number_nie_letter_error');
  }

  const numberWithReplacedFirstLetter = Number(replacedFirstLetter + withoutLetters);
  const expectedChar = NIE_STRING[numberWithReplacedFirstLetter % NIE_DIVISION_NUMBER];

  if (lastSymbol !== expectedChar) {
    return i18n.t('doc_number_nie_letter_error');
  }

  return true;
}

function validateDocNumberByDocumentType(
  number: string | null,
  docType: string | undefined,
) {
  if (docType && dniValidation.includes(docType)) {
    return validateDNI(String(number), docType);
  }

  if (docType && nieValidation.includes(String(docType))) {
    return validateNIE(String(number));
  }

  return true;
}

const maxNamesInputLength = 30;
const maxLength = 20;
const minLength = 2;

function getCustomFieldData(field: CustomField) {
  const language = getCurrentLocale().toUpperCase();
  const defaultLabel = Object.values(field.names?.[0])?.[0] || '';
  const localeLabel = field.names.find(name => {
    return name[language];
  })?.[language];
  const defaultPlaceholder = field.placeholders?.length
    ? Object.values(field.placeholders?.[0])?.[0]
    : '';
  const localePlaceholder = field?.placeholders?.length
    ? field?.placeholders?.find(placeholder => {
        return placeholder[language];
      })?.[language]
    : '';

  const label = localeLabel || defaultLabel;
  const placeholder = localePlaceholder || defaultPlaceholder;
  const name = field.name;

  return {label, placeholder, name};
}

function getLocationsAsOptions(locations: {
  count: number;
  results: Array<LocationType>;
}): SelectOptionType[] {
  const results = Array.isArray(locations) ? locations : locations?.results;
  if (!results) {
    return [];
  }

  return results?.map(l => {
    return {
      label: l?.country?.name,
      value: l?.country?.code,
    };
  });
}

function getIsBirthDateBeforeIssueDate(birthDate: Date | null, issueDate: Date | null) {
  if (birthDate && issueDate) {
    return dayjs(new Date(birthDate)).isBefore(new Date(issueDate));
  }
  return true;
}

function getIsDateBeforeToday(date: Date) {
  return dayjs(new Date()).isBefore(date);
}

function validateBirthDate(birthDate: Date, issueDate: Date | null) {
  let error = '';
  const isBeforeIssueDate = getIsBirthDateBeforeIssueDate(birthDate, issueDate);
  const isBeforeToday = getIsDateBeforeToday(birthDate);

  if (!isBeforeIssueDate) {
    error = i18n.t('cant_be_equal_or_after_than_issue_date');
  }
  if (isBeforeToday) {
    error += ` ${i18n.t('cant_be_equal_or_greater_than_today')}`;
  }

  return error || true;
}

function validateIssueDate(issueDate: Date, birthDate: Date | null) {
  let error = '';
  if (issueDate) {
    const isBeforeIssueDate = getIsBirthDateBeforeIssueDate(birthDate, issueDate);
    const isBeforeToday = getIsDateBeforeToday(issueDate);

    if (!isBeforeIssueDate) {
      error = i18n.t('cant_be_equal_or_smaller_than_birth_date');
    }
    if (isBeforeToday) {
      error += ` ${i18n.t('cant_be_equal_or_greater_than_today')}`;
    }
  }

  return error || true;
}

export {
  getDocNumberPattern,
  maxNamesInputLength,
  maxLength,
  minLength,
  getLocationsAsOptions,
  getCustomFieldData,
  validateBirthDate,
  validateIssueDate,
  goToExternalLink,
  validateDocNumberByDocumentType,
};
